import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, Divider, Skeleton, Stack, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import { ownerApi } from "../../../api/owner";
import { formatApiError } from "../../../api/client";
import OwnerInitiativeDrawer from "./center/OwnerInitiativeDrawer";
import OwnerCommitmentTimeDialog from "./center/OwnerCommitmentTimeDialog";
import { OwnerCommitmentDialog } from "./center/OwnerQuickDialogs";
import { insetSx, surfaceSx } from "./center/ownerCenterUi";
import OwnerProjectSummaryStrip from "./projects/OwnerProjectSummaryStrip";
import OwnerProjectWorkPanel from "./projects/OwnerProjectWorkPanel";
import OwnerProjectAside from "./projects/OwnerProjectAside";
import OwnerProjectEconomicsPanel from "./projects/OwnerProjectEconomicsPanel";
import OwnerProjectAgreementPanel from "./projects/OwnerProjectAgreementPanel";
import OwnerProjectWebProfitHealthPanel from "./projects/OwnerProjectWebProfitHealthPanel";
import {
  PROJECT_DOMAIN_LABELS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_STATUS_LABELS,
  dateOnlyLabel,
} from "./projects/ownerProjectUi";

function ProjectLoading() {
  return <Stack spacing={1.2}><Skeleton variant="rounded" height={92} /><Skeleton variant="rounded" height={112} /><Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,2fr) minmax(300px,1fr)" }, gap: 1.2 }}><Skeleton variant="rounded" height={420} /><Skeleton variant="rounded" height={420} /></Box></Stack>;
}

export default function OwnerProject360Page() {
  const { initiativeId } = useParams();
  const navigate = useNavigate();
  const [context, setContext] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [notFound, setNotFound] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [activityOpen, setActivityOpen] = React.useState(false);
  const [timeCommitment, setTimeCommitment] = React.useState(null);
  const [economics, setEconomics] = React.useState(null);
  const [economicsLoading, setEconomicsLoading] = React.useState(false);
  const [economicsError, setEconomicsError] = React.useState("");
  const [deleting, setDeleting] = React.useState(false);
  const [productHealth, setProductHealth] = React.useState(null);
  const [productHealthLoading, setProductHealthLoading] = React.useState(false);
  const [productHealthError, setProductHealthError] = React.useState("");

  const load = React.useCallback(async () => {
    if (!initiativeId) return;
    setLoading(true); setError(""); setNotFound(false);
    try {
      const response = await ownerApi.initiativeContext(initiativeId);
      const nextContext = response?.context || null;
      setContext(nextContext);
      setEconomics(nextContext?.economics || null);
      setEconomicsError(nextContext?.metadata?.economicsError ? "No pude calcular la economía del proyecto." : "");
    } catch (err) {
      setContext(null);
      if (err?.status === 404) setNotFound(true);
      else setError(formatApiError(err, "No pude cargar este proyecto."));
    } finally {
      setLoading(false);
    }
  }, [initiativeId]);

  React.useEffect(() => { load(); }, [load]);

  const loadProductHealth = React.useCallback(async () => {
    if (!initiativeId) return;
    setProductHealthLoading(true);
    setProductHealthError("");
    try {
      const response = await ownerApi.initiativeProductHealth(initiativeId, { days: 30 });
      setProductHealth(response?.health || null);
    } catch (err) {
      setProductHealth(null);
      setProductHealthError(formatApiError(err, "No pude leer el uso de WebProfit para este proyecto."));
    } finally {
      setProductHealthLoading(false);
    }
  }, [initiativeId]);

  React.useEffect(() => { loadProductHealth(); }, [loadProductHealth]);

  const retryEconomics = React.useCallback(async () => {
    if (!initiativeId) return;
    setEconomicsLoading(true); setEconomicsError("");
    try {
      const response = await ownerApi.initiativeEconomics(initiativeId);
      setEconomics(response?.economics || null);
    } catch (err) {
      setEconomicsError(formatApiError(err, "No pude calcular la economía del proyecto."));
    } finally {
      setEconomicsLoading(false);
    }
  }, [initiativeId]);

  const goBack = () => {
    if (typeof window !== "undefined" && Number(window.history?.state?.idx || 0) > 0) navigate(-1);
    else navigate("/dashboard/owner");
  };

  const initiative = context?.initiative || null;
  const client = context?.client || null;

  const deleteProject = async () => {
    if (!initiative || deleting) return;
    const confirmed = window.confirm(`¿Eliminar ${initiative.name}?\n\nSe eliminará la ficha, contrato y bloques del proyecto. Los movimientos, pagos, tareas y horas trabajadas NO se borran: quedan desvinculados para conservar el historial.`);
    if (!confirmed) return;
    setDeleting(true);
    setError("");
    try {
      await ownerApi.deleteInitiative(initiativeId);
      navigate("/dashboard/owner/proyectos", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "No pude eliminar el proyecto."));
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 1580, mx: "auto", pb: 1 }}>
      <Button startIcon={<ArrowBackRoundedIcon />} onClick={goBack} sx={{ mb: 1, px: .4, textTransform: "none", fontWeight: 900 }}>Volver</Button>

      {loading ? <ProjectLoading /> : null}
      {!loading && notFound ? <Box sx={{ ...surfaceSx, p: { xs: 2, md: 3 }, textAlign: "center" }}><Typography sx={{ fontSize: "1.15rem", fontWeight: 1000 }}>Este proyecto no existe o ya no está disponible.</Typography><Typography sx={{ mt: .45, color: "text.secondary", fontSize: ".82rem" }}>Puede haber sido eliminado o no pertenecer a tu espacio Owner.</Typography><Button variant="contained" onClick={() => navigate("/dashboard/owner")} sx={{ mt: 1.4, textTransform: "none", fontWeight: 950 }}>Volver a Owner</Button></Box> : null}
      {!loading && error ? <Alert severity="error" action={<Button size="small" onClick={load} sx={{ textTransform: "none", fontWeight: 900 }}>Reintentar</Button>}>{error}</Alert> : null}

      {!loading && context && initiative ? <Stack spacing={1.2}>
        <Box sx={{ ...surfaceSx, p: { xs: 1.6, md: 2 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.3} alignItems={{ md: "flex-start" }}>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={.5} flexWrap="wrap" useFlexGap alignItems="center">
                <Typography component="h1" sx={{ fontSize: { xs: "1.55rem", md: "2rem" }, fontWeight: 1000, letterSpacing: "-.035em" }}>{initiative.name}</Typography>
                <Chip size="small" label={PROJECT_STATUS_LABELS[initiative.status] || "Estado definido"} sx={{ fontWeight: 900 }} />
                <Chip size="small" variant="outlined" label={`Prioridad ${PROJECT_PRIORITY_LABELS[initiative.priority] || "normal"}`} sx={{ fontWeight: 850 }} />
                {initiative.domain ? <Chip size="small" variant="outlined" label={PROJECT_DOMAIN_LABELS[initiative.domain] || "Otro"} /> : null}
              </Stack>
              <Typography sx={{ mt: .45, color: "text.secondary", fontSize: ".82rem" }}>{client ? client.name : "Sin cliente vinculado"} · {initiative.startedAt ? `Inicio ${dateOnlyLabel(initiative.startedAt)}` : "Sin fecha de inicio"}</Typography>
            </Box>
            <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap>
              {initiative.driveFolderUrl ? <Button variant="outlined" startIcon={<FolderOpenRoundedIcon />} component="a" href={initiative.driveFolderUrl} target="_blank" rel="noopener noreferrer" sx={{ textTransform: "none", fontWeight: 950 }}>Abrir Drive</Button> : null}
              <Button color="error" variant="outlined" startIcon={<DeleteOutlineRoundedIcon />} disabled={deleting} onClick={deleteProject} sx={{ textTransform: "none", fontWeight: 950 }}>{deleting ? "Eliminando…" : "Eliminar"}</Button>
              <Button variant="outlined" startIcon={<EditRoundedIcon />} onClick={() => setEditOpen(true)} sx={{ textTransform: "none", fontWeight: 950 }}>Editar</Button>
              <Button variant="contained" startIcon={<AddTaskRoundedIcon />} onClick={() => setActivityOpen(true)} sx={{ textTransform: "none", fontWeight: 950 }}>Nueva actividad</Button>
            </Stack>
          </Stack>
        </Box>

        <OwnerProjectSummaryStrip context={context} />
        <OwnerProjectEconomicsPanel economics={economics} loading={economicsLoading} error={economicsError} onRetry={retryEconomics} onNavigate={(path) => path && navigate(path)} />
        <OwnerProjectAgreementPanel initiativeId={initiativeId} />
        <OwnerProjectWebProfitHealthPanel health={productHealth} loading={productHealthLoading} error={productHealthError} onRetry={loadProductHealth} onEditProject={() => setEditOpen(true)} />

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0,2fr) minmax(310px,1fr)" }, gap: 1.2, alignItems: "start" }}>
          <Stack spacing={1.2}>
            <Box sx={{ ...surfaceSx, p: { xs: 1.5, md: 1.8 } }}>
              <Typography sx={{ fontWeight: 1000 }}>Qué estamos buscando</Typography>
              <Box sx={{ ...insetSx, mt: .85, p: 1.15 }}><Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 950 }}>OBJETIVO</Typography><Typography sx={{ mt: .3, fontSize: ".86rem", whiteSpace: "pre-wrap" }}>{initiative.outcome || "Todavía no definiste el objetivo."}</Typography></Box>
              <Divider sx={{ my: 1 }} />
              <Typography sx={{ color: "text.secondary", fontSize: ".66rem", fontWeight: 950 }}>PRÓXIMO PASO RESUMIDO</Typography><Typography sx={{ mt: .3, fontSize: ".86rem", whiteSpace: "pre-wrap" }}>{initiative.nextStep || "No hay un próximo paso resumido."}</Typography>
            </Box>
            <OwnerProjectWorkPanel commitments={context.commitments} meetings={context.meetings} onNewActivity={() => setActivityOpen(true)} onTime={setTimeCommitment} onDone={async (task) => { if (!task?.id) return; try { await ownerApi.updateCommitment(task.id, { status: "done" }); if (task.projectRole === "blocker") await ownerApi.updateInitiative(initiativeId, { workflow: { state: "steady", waitingFor: "", blockedBy: "" } }); await load(); } catch (err) { setError(formatApiError(err, "No pude completar esta actividad.")); } }} />
          </Stack>
          <OwnerProjectAside context={context} onNavigate={(path) => path && navigate(path)} />
        </Box>
      </Stack> : null}

      <OwnerInitiativeDrawer open={editOpen} initiative={initiative} onClose={() => setEditOpen(false)} onChanged={async () => { setEditOpen(false); await Promise.all([load(), loadProductHealth()]); }} />
      <OwnerCommitmentDialog open={activityOpen} onClose={() => setActivityOpen(false)} onSaved={async () => { setActivityOpen(false); await load(); }} initiatives={initiative ? [initiative] : []} preferredInitiative={initiative} person={client} />
      <OwnerCommitmentTimeDialog open={Boolean(timeCommitment)} commitment={timeCommitment} onClose={() => setTimeCommitment(null)} onSaved={async () => { setTimeCommitment(null); await load(); }} />
    </Box>
  );
}
